import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeEvent, ChangeEventHandler } from "react";

/**
 * Un champ qui accepte le texte venu d'ailleurs — Wispr Flow, la dictée du
 * système, un gestionnaire de mots de passe, un presse-papiers.
 *
 * Le patron, le 17 septembre : « fais en sorte que l'app Whisper Flow puisse
 * accéder à l'app pour la dictée ». Ces outils vivent hors du navigateur :
 * rien à autoriser côté serveur, ils écrivent dans le champ qui a le focus.
 * Reste un piège, et il est dans React.
 *
 * **React tient un « suiveur » de la valeur de chaque champ contrôlé.** Quand
 * un outil écrit `champ.value = "…"` puis déclenche un événement `input`, il
 * passe par l'accesseur que React a posé sur l'élément : le suiveur est mis à
 * jour *avant* l'événement, React compare, ne voit aucun changement, et
 * n'appelle jamais `onChange`. Le texte dicté s'affiche une fraction de
 * seconde, puis le rendu suivant le remplace par l'ancien état. C'est le
 * défaut classique, et il ne se voit qu'avec un outil externe.
 *
 * Ce module le répare, sans jamais se mettre en travers d'une saisie normale :
 *
 *   · un événement `input` est arrivé mais React n'a **pas** appelé `onChange` ?
 *     La valeur du champ est réinjectée de façon que React la voie. C'est le
 *     seul cas réparé — un champ qui transforme ce qu'on tape (une limite, un
 *     formatage) appelle bien `onChange`, et n'est donc pas touché ;
 *   · le champ perd le focus, ou la fenêtre revient au premier plan, et son
 *     contenu ne correspond pas à l'état ? Même réparation. C'est ce qui
 *     rattrape un outil qui écrit sans déclencher le moindre événement ; le
 *     `blur` précède le clic sur « Enregistrer », donc le texte est adopté
 *     avant l'envoi.
 */

type Champ = HTMLInputElement | HTMLTextAreaElement;

/**
 * L'accesseur d'origine du navigateur, pris sur le prototype : celui de
 * l'élément a été remplacé par React, et c'est précisément ce qu'on contourne.
 */
function setterNatif(el: Champ): ((v: string) => void) | undefined {
  const proto =
    typeof HTMLTextAreaElement !== "undefined" && el instanceof HTMLTextAreaElement
      ? HTMLTextAreaElement.prototype
      : HTMLInputElement.prototype;
  return Object.getOwnPropertyDescriptor(proto, "value")?.set;
}

export function useChampExterne<T extends Champ>({
  value,
  onChange,
}: {
  value?: unknown;
  onChange?: ChangeEventHandler<T>;
}): { ref: (el: T | null) => void; onChange: ChangeEventHandler<T> } {
  const [noeud, setNoeud] = useState<T | null>(null);

  // Ce que React croit que le champ contient, à l'instant.
  const attendue = useRef(value);
  attendue.current = value;
  const rappel = useRef(onChange);
  rappel.current = onChange;

  const vuParReact = useRef(false);
  const enReparation = useRef(false);

  /**
   * `avale` : un événement `input` est bien arrivé et React ne l'a pas vu. On
   * répare alors même sans savoir ce que React attendait — c'est le cas d'un
   * champ non contrôlé, comme ceux du formulaire de devis, où React ne tient
   * aucune valeur mais où `onChange` doit malgré tout partir.
   */
  const reconcilier = useCallback(
    (avale = false) => {
      const el = noeud;
      const prevue = attendue.current;
      const controle = typeof prevue === "string";
      if (!el || enReparation.current) return;
      if (!controle && !avale) return;
      const presente = el.value;
      if (controle && presente === prevue) return;
      const setter = setterNatif(el);
      if (!setter) return;

      enReparation.current = true;
      try {
        // Deux gestes : mettre dans le suiveur une valeur différente de celle
        // du champ (par l'accesseur de React), puis réécrire le texte réel par
        // l'accesseur natif — que le suiveur ne voit pas. L'écart devient
        // visible, et l'événement le fait remonter jusqu'à `onChange`.
        const leurre = controle ? (prevue as string) : presente === "" ? " " : "";
        el.value = leurre;
        setter.call(el, presente);
        el.dispatchEvent(new Event("input", { bubbles: true }));
      } finally {
        enReparation.current = false;
      }
    },
    [noeud],
  );

  useEffect(() => {
    if (!noeud) return;

    const apresLeTour = () => window.setTimeout(() => reconcilier(), 0);

    const surInput = () => {
      if (enReparation.current) return;
      vuParReact.current = false;
      // Après le tour de React : s'il a traité l'événement, `onChange` a
      // marqué le passage et il n'y a rien à faire.
      window.setTimeout(() => {
        if (!vuParReact.current) reconcilier(true);
      }, 0);
    };

    noeud.addEventListener("input", surInput);
    noeud.addEventListener("focusout", apresLeTour);
    window.addEventListener("focus", apresLeTour);
    document.addEventListener("visibilitychange", apresLeTour);
    return () => {
      noeud.removeEventListener("input", surInput);
      noeud.removeEventListener("focusout", apresLeTour);
      window.removeEventListener("focus", apresLeTour);
      document.removeEventListener("visibilitychange", apresLeTour);
    };
  }, [noeud, reconcilier]);

  const surChangement = useCallback((e: ChangeEvent<T>) => {
    vuParReact.current = true;
    rappel.current?.(e);
  }, []);

  return { ref: setNoeud, onChange: surChangement };
}
